import { create } from 'zustand';
import { apiUrl } from '../../core/api/api-url';
import { withQuery } from '../../core/api/query';
import { appClient } from '../../core/api/http';
import { dismissRefused, submitCapture } from '../../core/outbox/outbox.store';
import { createMountGuard } from '../../core/reach/mount-guard';
import { createQuietRefresh } from '../../core/reach/quiet-refresh';
import {
  restoreLastCleared, shownFromStorageNow, useShownFromStorage, type StoredViewSummary,
} from '../../core/stored-views/presentation.store';
import { useWritable } from '../../core/stored-views/use-writable';
import { recordPageOpened } from '../../core/stored-views/last-opened';
import { toApiError, type ApiError } from '../../core/api/api-error';
import { completedFetchSize } from '../../core/preferences/defaults';
import { completionsShown, effectiveZone } from '../../core/preferences/preferences.store';
import { clock } from '../../core/time/clock';
import { calendarDayIn } from '../../core/time/calendar-day';
import type {
  ActionSummary,
  ContextSummary,
  PagedActions,
  PagedContexts,
  PagedProjects,
  ProjectSummary,
  QuickAddRequest,
  QuickCaptureResult,
} from '../../core/api/types';
import { sectionsOf, type ActionSection } from '../../shared/actions/model/sections';
import { foldIn, type OptimisticHost, type Reread } from '../../shared/actions/optimistic';
import { submitOrWithdraw } from '../../shared/actions/queued-change';
import { lookupsOf, type ContainerLookups } from '../../shared/containers/container-lookups';

/** The same fixed page the home and container screens ask for — one request, not one per screenful. */
const MAX_PAGE = 500;

/**
 * What the tag view holds. Note what is *not* here: a `kind`, and an "other" container list.
 *
 * This is the whole reason 011 has its own store rather than a third arm on `container.store.ts`
 * (research R-006). `ContainerData` is a two-arm discriminated union precisely so that narrowing on
 * `kind` also narrows the "other" list — a project page carries contexts, a context page carries
 * projects. A tag has no other list: it carries both, and for a different reason (`groupActions`
 * needs a name for a heading and a name for a row's project label, not a picker). Adding a third
 * arm would have made `kind` stop correlating with anything, which is the property that union
 * exists for.
 */
export interface TagData {
  /** As the practitioner typed it into the URL — the stored casing arrives on the rows, not here. */
  readonly name: string;
  /** Every open action carrying the tag, in the server's order (FR-018); never re-sorted. */
  readonly actions: readonly ActionSummary[];
  readonly completedRecent: readonly ActionSummary[];
  readonly contexts: readonly ContextSummary[];
  readonly projects: readonly ProjectSummary[];
}

/**
 * One value, so the view never reasons about which fields are meaningful — the shape
 * `container.store.ts` and `home.store.ts` already use. `not-found` is a state of its own because a
 * name the practitioner does not own is a stale link, not an unreachable API (research R-005), and
 * an owned tag carrying nothing is `ready` with empty lists rather than `not-found`: an emptied tag
 * is not a broken one (FR-020).
 */
export type TagStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly data: TagData | null }
  | { readonly kind: 'ready'; readonly data: TagData }
  | { readonly kind: 'error'; readonly error: ApiError; readonly data: TagData | null }
  | { readonly kind: 'not-found' };

function dataOf(status: TagStatus): TagData | null {
  switch (status.kind) {
    case 'ready':
    case 'loading':
    case 'error':
      return status.data;
    default:
      return null;
  }
}

interface TagState {
  readonly status: TagStatus;
  /** 010 FR-006: the practitioner's calendar day, reduced to midnight UTC once, on this page's path. */
  readonly today: Date;
  /** 010: the raw instant. Staleness measures elapsed time, so it must not be handed `today`. */
  readonly now: Date;
  /** The last refused write. Separate from `status`: a refusal must not blank the page. */
  readonly problem: ApiError | null;
  /** Rows with a write in flight — per id, so two changes to two rows never wait on each other. */
  readonly pending: readonly number[];
  /** The capture box's own busy state; see `home.store.ts`'s `HomeState.capturing`
   *  for the fuller comment. Independent of `status`. */
  readonly capturing: boolean;
  /**
   * 036 (issue 234) — the tag name `loadTag()` was actually asked to fetch, set before any of its
   * reads is even attempted. `tagQuickAdd()` reads this when `status.data` is `null` — see
   * `container.store.ts`'s identical `ContainerState.requested` for the fuller reasoning; a tag
   * page's "container" is the name, which the route already supplies.
   */
  readonly requested: string | null;
}

const INITIAL: TagState = {
  status: { kind: 'idle' },
  today: new Date(0),
  now: new Date(0),
  problem: null,
  pending: [],
  capturing: false,
  requested: null,
};

const store = create<TagState>()(() => INITIAL);

// --- reading -----------------------------------------------------------------------------------

const dataNow = (): TagData | null => dataOf(store.getState().status);

export const tagStatus = {
  use: (): TagStatus => store((s) => s.status),
  get: (): TagStatus => store.getState().status,
};
export const tagData = {
  use: (): TagData | null => store((s) => dataOf(s.status)),
  get: dataNow,
};
export const tagProblem = {
  use: (): ApiError | null => store((s) => s.problem),
  get: (): ApiError | null => store.getState().problem,
};
export const tagToday = {
  use: (): Date => store((s) => s.today),
  get: (): Date => store.getState().today,
};
export const tagNow = {
  use: (): Date => store((s) => s.now),
  get: (): Date => store.getState().now,
};

/** The capture box's own busy state; see `TagState.capturing`. */
export const tagCapturing = {
  use: (): boolean => store((s) => s.capturing),
  get: (): boolean => store.getState().capturing,
};

/** The same three sections a container screen presents, from the same `action.status` (FR-016). */
const sectionsFrom = (data: TagData | null): readonly ActionSection[] => (data ? sectionsOf(data.actions) : []);

export const tagSections = {
  use: (): readonly ActionSection[] => sectionsFrom(store((s) => dataOf(s.status))),
  get: (): readonly ActionSection[] => sectionsFrom(dataNow()),
};

/** 016 FR-002/FR-003: the two lists the row editor's pickers derive from (see `lookupsOf`). */
export const tagLookups = {
  use: (): ContainerLookups | null => lookupsOf(store((s) => dataOf(s.status))),
  get: (): ContainerLookups | null => lookupsOf(dataNow()),
};

export const isTagRowPending = (id: number): boolean => store.getState().pending.includes(id);

/** 036 T092 — a real subscription for `pending`; see `home.store.ts`'s `usePendingIds` for why this
 *  is needed now that `setCompleted`/`toggleStar`/`defer` can settle with no other state changing. */
export const useTagRowPendingIds = (): readonly number[] => store((s) => s.pending);

// --- writing -----------------------------------------------------------------------------------

const contextsUrl = (): string => apiUrl('/api/v1/contexts');
const projectsUrl = (): string => apiUrl('/api/v1/projects');
const actionsUrl = (): string => apiUrl('/api/v1/actions');

const pageUrl = (url: string, extra: Record<string, string | number> = {}): string =>
  withQuery(url, { pageSize: MAX_PAGE, ...extra });

/**
 * 036 (issue 221, FR-021): this screen's two own reads, for `core/stored-views/`'s allow-list and
 * for `useTagWritable` below. Its container-lookup reads are already on the allow-list under home's
 * own entries.
 *
 * 036 (issue 259, C3) — the ONE place these two URLs are built; `readActionsAll`/
 * `readActionsCompleted` below call straight into this array. Before this fix each built its own
 * copy, and a one-parameter drift between a read and its matching entry here would have meant this
 * screen's stored view was never found offline (issue 259's own finding).
 *
 * Exported since issue 222: `core/stored-views/fetch-ahead.ts` calls this for every tag it
 * prefetches, rather than a third definition of a tag's own two requests.
 */
export function tagViewUrls(name: string): readonly string[] {
  return [
    withQuery(actionsUrl(), { view: 'all', tag: name, pageSize: MAX_PAGE }),
    withQuery(actionsUrl(), { view: 'completed', tag: name, pageSize: completedFetchSize(completionsShown.get()) }),
  ];
}

/**
 * The tag term is the NAME, not an id — the URL is all this page has, and the server resolves it
 * (research R-015). Every other part of the request is a container page's request unchanged,
 * which is what makes availability the same computation here as there (FR-017).
 *
 * 036 (issue 221): `confirmingWrite` sets `requireLiveAnswer` — see `container.store.ts`'s
 * `readActionsAll`'s identical note.
 */
const readActionsAll = (name: string, confirmingWrite = false): Promise<PagedActions> =>
  appClient().get<PagedActions>(tagViewUrls(name)[0]!, { requireLiveAnswer: confirmingWrite });

/** 009 FR-007 verbatim: fetch = max(20, N), show = N. The screen never picks its own number.
 *  `confirmingWrite`, same reasoning as `readActionsAll`'s own note. */
const readActionsCompleted = (name: string, confirmingWrite = false): Promise<PagedActions> =>
  appClient().get<PagedActions>(tagViewUrls(name)[1]!, { requireLiveAnswer: confirmingWrite });

/** FR-018/FR-024 — whether either of the CURRENTLY-SHOWN tag's own reads is drawn from storage. */
export const tagStoredView = {
  use: (): StoredViewSummary => {
    const d = tagData.use();
    return useShownFromStorage(d ? tagViewUrls(d.name) : []);
  },
  get: (): StoredViewSummary => {
    const d = dataNow();
    return shownFromStorageNow(d ? tagViewUrls(d.name) : []);
  },
};

/** FR-035, research R-022 — false while any of the shown tag's own reads is drawn from storage.
 *  Nothing on this screen is actually gated by it today — every write here (complete, star, defer,
 *  tags, edit) is named by FR-026 — but `action-row.tsx`'s own "make recurring" link reads it, the
 *  same as every other screen that renders an open action row. */
export function useTagWritable(): boolean {
  const d = tagData.use();
  return useWritable(d ? tagViewUrls(d.name) : []);
}

/**
 * The ONE place `pageUrl(contextsUrl())`/`pageUrl(projectsUrl(), {state:
 * 'all'})` are written. Before this fix `loadTag()` and `quietRefreshTag()` each built these two
 * requests by hand, a second copy of the exact literals `only-request-path-once.spec.ts` now
 * fails on. Combined into one read (rather than kept as two separately-named ones, the way
 * `readActionsAll`/`readActionsCompleted` are) because nothing here ever needs to know WHICH of the
 * two failed — `loadTag()`'s own `Promise.allSettled` treats a rejection from either exactly alike.
 */
const readTagLookups = async (): Promise<Pick<TagData, 'contexts' | 'projects'>> => {
  const [contexts, projects] = await Promise.all([
    appClient().get<PagedContexts>(pageUrl(contextsUrl())),
    appClient().get<PagedProjects>(pageUrl(projectsUrl(), { state: 'all' })),
  ]);
  return { contexts: contexts.items, projects: projects.items };
};

/** The three fixed requests `loadTag()` and a quiet refresh both need, combined into `TagData`. */
const readTagData = async (name: string): Promise<TagData> => {
  const [all, completed, lookups] = await Promise.all([
    readActionsAll(name), readActionsCompleted(name), readTagLookups(),
  ]);
  return { name, actions: all.items, completedRecent: completed.items, contexts: lookups.contexts, projects: lookups.projects };
};

/**
 * A capture refused while this page is still showing the SAME tag it was typed on can be shown here
 * and removed from the outbox (`dismissRefused`); one refused after the practitioner navigated away
 * — to another screen, or to a DIFFERENT tag — must not surface `problem` on a page the line was
 * never typed on (`core/reach/mount-guard.ts`).
 */
const tagMountGuard = createMountGuard<string>();
export const registerTagMounted = tagMountGuard.register;

/** `store.setState`'s ready-data half, with no side effect on the quiet-refresh generation. */
const applyReady = (data: TagData): void => store.setState({ status: { kind: 'ready', data } });

/**
 * `core/reach/quiet-refresh.ts` — see that file, and `container.store.ts`'s identical note: without
 * this, a stale answer for the tag the practitioner just navigated AWAY from could land on the
 * DIFFERENT tag they navigated to — both read `ready`. `loadTag()` touching the guard on every
 * navigation closes both gaps in the same mechanism.
 */
const quietRefresh = createQuietRefresh<TagData>({
  ready: () => store.getState().status.kind === 'ready',
  busy: () => store.getState().pending.length > 0,
  read: () => readTagData(dataNow()!.name),
  apply: applyReady,
  busyDiscard: () => restoreLastCleared(tagViewUrls(dataNow()!.name)),
});

/** Every local change to the screen's data goes through this, so the quiet refresh always knows one
 *  happened. */
const setReady = (data: TagData): void => {
  quietRefresh.touched();
  applyReady(data);
};

/**
 * The same five operations `container.store.ts` adapts itself to (research R-006 lists
 * `optimistic.ts` among the parts 011 reuses). `dataNow()!` is safe for the reason stated there:
 * `mutate` only reaches it after `isReady()` has answered true.
 */
const writeHost: OptimisticHost<TagData> = {
  isReady: () => store.getState().status.kind === 'ready',
  pending: () => store.getState().pending,
  data: () => dataNow()!,
  setData: setReady,
  begin: (id) => {
    store.setState({ pending: [...store.getState().pending, id], problem: null });
  },
  fail: (problem) => store.setState({ problem }),
  release: (id) => {
    store.setState({ pending: store.getState().pending.filter((p) => p !== id) });
    quietRefresh.settled();
  },
};

/**
 * Which of the two views a write invalidates differs per write, so they are values rather than a
 * fixed pair. Both are keyed on the NAME, which is all this page holds — the same term `loadTag`
 * sends, so a re-read after a write is byte-for-byte the request that filled the page.
 */
type WriteReread =
  | { readonly kind: 'all'; readonly paged: PagedActions }
  | { readonly kind: 'completed'; readonly paged: PagedActions };

const rereadAllFor = (name: string): Reread<TagData, WriteReread> => ({
  read: async () => ({ kind: 'all', paged: await readActionsAll(name, true) }),
  apply: (data, result) => (result.kind === 'all' ? { ...data, actions: result.paged.items } : data),
});
const rereadCompletedFor = (name: string): Reread<TagData, WriteReread> => ({
  read: async () => ({ kind: 'completed', paged: await readActionsCompleted(name, true) }),
  apply: (data, result) => (result.kind === 'completed' ? { ...data, completedRecent: result.paged.items } : data),
});

/** A row on this page can be open or recently-completed, so a star has to find it in either list. */
const replaceAnywhere = (data: TagData, updated: ActionSummary): TagData => ({
  ...data,
  actions: data.actions.map((a) => (a.id === updated.id ? updated : a)),
  completedRecent: data.completedRecent.map((a) => (a.id === updated.id ? updated : a)),
});

/**
 * Four reads at once, settled rather than raced (`allSettled`, not `all`), so a 404 on the
 * action read is observed as a 404 even when a slower request rejects first.
 */
export async function loadTag(name: string): Promise<void> {
  // A fresh load (including a navigation to a DIFFERENT tag) supersedes anything a quiet refresh
  // has in flight, whether or not `status` ever leaves `ready`.
  quietRefresh.touched();
  // 036 (issue 222, FR-023) — see `container.store.ts`'s `load()` for the identical reasoning.
  void recordPageOpened('tag', name);
  const carried = dataOf(store.getState().status);
  // 010 FR-006: one reduction, here, on this page's path.
  const instant = clock();
  store.setState({
    status: { kind: 'loading', data: carried },
    today: calendarDayIn(instant, effectiveZone.get()),
    now: instant,
    problem: null,
    // 036 (issue 234): before either read below is even attempted — see `TagState.requested`.
    requested: name,
  });

  const [allResult, completedResult, lookupsResult] = await Promise.allSettled([
    readActionsAll(name),
    readActionsCompleted(name),
    readTagLookups(),
  ]);

  if (allResult.status === 'rejected') {
    const error = toApiError(allResult.reason);
    // The 404 is the tag's own — it is the only read here keyed on the name, so no other
    // rejection can mean "no such tag" and none is allowed to claim it.
    store.setState(error.status === 404
      ? { status: { kind: 'not-found' } }
      : { status: { kind: 'error', error, data: carried } });
    return;
  }

  for (const rest of [completedResult, lookupsResult]) {
    if (rest.status === 'rejected') {
      store.setState({ status: { kind: 'error', error: toApiError(rest.reason), data: carried } });
      return;
    }
  }

  const lookups = (lookupsResult as PromiseFulfilledResult<Pick<TagData, 'contexts' | 'projects'>>).value;
  applyReady({
    name,
    actions: allResult.value.items,
    completedRecent: (completedResult as PromiseFulfilledResult<PagedActions>).value.items,
    contexts: lookups.contexts,
    projects: lookups.projects,
  });
}

/**
 * The QUIET re-read `resync` (FR-036) must trigger, not `loadTag()` itself. See `home.store.ts`'s
 * `quietRefreshHome` for the fuller reasoning. Re-reads the same requests for whichever tag is
 * CURRENTLY shown (its name read off `status.data` itself), replacing the data only once every one
 * of them has answered — never touching `status`'s kind, `today`/`now`, `pending` or `problem`. Any
 * failure at all is treated alike, and quietly — there is no `not-found` transition to protect here,
 * unlike `loadTag()`.
 */
export const quietRefreshTag = quietRefresh.run;

export function dismissTagProblem(): void {
  store.setState({ problem: null });
}

/**
 * Folds back the action a tag write returned (011 FR-024). Nothing is re-read: no ordering rule
 * and no availability rule reads a tag, so tagging a row cannot move it.
 *
 * **A row is not dropped when the write removes the tag this page is keyed on.** The page would
 * be re-fetched on the next visit and the row would be gone then; removing it under the
 * practitioner's cursor, in the same gesture that produced it, is the worse of the two — it
 * looks like the action was deleted. The list is the server's answer to one question, asked once.
 */
export function adoptTagAction(updated: ActionSummary): void {
  const status = store.getState().status;
  if (status.kind !== 'ready') return;
  setReady(replaceAnywhere(status.data, updated));
}

/**
 * Capture in place (FR-020a). The line goes to the server EXACTLY as typed and the tag travels
 * beside it in `tag` — never appended to the text as a `#name` marker, which is 007 research
 * R-007's rule for `@context` and `~project` applied to the third marker: the practitioner's
 * own words are theirs, and a rename would otherwise leave a stale name inside their sentence.
 *
 * What it deliberately does NOT send is a default context. A tag is not a container and has
 * none to lend, so a line with no `@` is refused here exactly as it would be from the home
 * page (FR-020a) — the refusal surfaces as `problem`, leaving the page as it stands.
 *
 * **036 (issue 207): routed through `submit()`** — see `home.store.ts`'s `quickAdd` for what each
 * outcome does here. No longer conditioned on `status.kind === 'ready'` (research F-005);
 * `dataNow()` carries the tag's name forward through `'error'` too.
 *
 * **The capture is always submitted; its re-read is only ever applied while the
 * store is STILL `ready` for the SAME tag it was submitted against** (`container.store.ts`'s
 * `quickAdd` makes the identical two-part argument for a container's id — this page's "container"
 * is the tag name).
 *
 * **036 (issue 234): when `data` is `null` (nothing has ever loaded here — the no-stored-copy
 * screen), `name` falls back to `TagState.requested`**, the route's own name `loadTag()` recorded
 * before its reads were ever attempted. Capture needs no more than the name to send `tag: name` —
 * it was never the read's own answer that supplied it, so there is no reason the read had to have
 * succeeded first.
 */
/**
 * `capturing` guards this function itself, independent of `status`; see
 * `home.store.ts`'s `quickAdd` header for the fuller comment (the identical shape applies here,
 * issue 257 A3 included: `capturing` clears the moment the line is durably queued, not once the send
 * outcome is known).
 */
export async function tagQuickAdd(line: string): Promise<void> {
  const text = line.trim();
  if (text.length === 0) return;
  if (store.getState().capturing) return;
  const data = dataNow();
  const name = data ? data.name : store.getState().requested;
  if (name === null) return;
  const stillTheSameTag = (): boolean =>
    store.getState().status.kind === 'ready' && dataNow()?.name === name;

  store.setState({ problem: null, capturing: true });
  let queued = false;
  let created: QuickCaptureResult;
  try {
    const body: QuickAddRequest = { line: text, tag: name };
    const outcome = await submitCapture<QuickCaptureResult>(body, {
      onQueued: () => {
        queued = true;
        store.setState({ capturing: false });
      },
    });
    if (outcome.kind === 'waiting') return;
    if (outcome.kind === 'refused') {
      // Shown here, and the row removed from the outbox, only while this is still the page showing
      // the SAME tag it was typed on — otherwise the row stays refused so the unsent count still
      // tells the practitioner (`core/reach/mount-guard.ts`).
      if (tagMountGuard.isCurrent(name)) {
        store.setState({ problem: outcome.problem });
        void dismissRefused(outcome.seq, outcome.practitionerId);
      }
      return;
    }
    created = outcome.result;
  } catch (error) {
    store.setState({ problem: toApiError(error) });
    return;
  } finally {
    if (!queued) store.setState({ capturing: false });
  }

  if (!stillTheSameTag()) return;

  try {
    const [all, completed] = await Promise.all([readActionsAll(name, true), readActionsCompleted(name, true)]);
    if (!stillTheSameTag()) return;
    const current = dataNow()!;
    setReady({
      ...current,
      // A container the line created is added to the name lists rather than re-read: the page
      // needs it immediately (a heading is named from it, and a row's badge reads it), and the
      // create response already carries the whole summary, counts included.
      contexts: created.createdContext ? [...current.contexts, created.createdContext] : current.contexts,
      projects: created.createdProject ? [...current.projects, created.createdProject] : current.projects,
      actions: all.items,
      completedRecent: completed.items,
    });
  } catch (error) {
    if (stillTheSameTag()) store.setState({ problem: toApiError(error) });
  }
}

/**
 * The row controls (036 T092, routed through the outbox — `queued-change.ts`'s header comment has
 * the two review findings this closes). **No optimistic move**: constitution II / issue 209's rule
 * is that a row shows the practitioner's own change and nothing that follows from it, so the row
 * stays exactly where it is until the write actually lands — the ticked appearance while unsent is
 * `action-row.tsx`'s overlay (T091), not this store's data. Both views are re-read once it does:
 * completing one action can unblock another this page has not seen, and which section a reopened
 * row rejoins is the server's `status` to say.
 */
export function tagSetCompleted(action: ActionSummary, completed: boolean): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const name = status.data.name;

  return submitOrWithdraw(
    writeHost,
    action.id,
    'isCompleted',
    { isCompleted: completed },
    { isCompleted: completed, description: action.description },
    (current) => current,
    [rereadAllFor(name), rereadCompletedFor(name)],
  ).then(() => undefined);
}

/**
 * 016 FR-018: the row's editor has saved — see `home.store.ts`'s `editSaved` for why the PATCH
 * is the editor's, and `foldIn` for why a committed write is folded in rather than mutated.
 * Both views are re-read: the tag view
 * is the container read with one more term (research R-001), so an edit moves a row here for
 * exactly the reasons it moves one there.
 *
 * The edit carries none of the action's tags, so nothing here can untag a row (SC-002); what
 * the re-read returns is what the server still holds under this name.
 */
export function tagEditSaved(updated: ActionSummary): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const name = status.data.name;

  return foldIn(writeHost, updated.id, (data) => replaceAnywhere(data, updated), [
    rereadAllFor(name),
    rereadCompletedFor(name),
  ]);
}

/**
 * A star (036 T092, routed through the outbox) changes neither order nor availability, so nothing is
 * re-read once it lands. No optimistic move: the starred appearance while unsent is the overlay's
 * (T091), same reasoning as `tagSetCompleted` above.
 */
export function tagToggleStar(action: ActionSummary): Promise<void> {
  if (store.getState().status.kind !== 'ready') return Promise.resolve();
  const isStarred = !action.isStarred;
  return submitOrWithdraw(
    writeHost,
    action.id,
    'isStarred',
    { isStarred },
    { isStarred },
    (data, { action: updated }) => replaceAnywhere(data, updated),
    [],
  ).then(() => undefined);
}

/**
 * Deferring (036 T092, routed through the outbox). Already had no optimistic list edit before this
 * slice, for `container.store.ts`'s reason: the four sections are one array partitioned by
 * `sections.ts` on the server-derived `status`, and where a deferred row lands next is exactly what
 * this store must not guess. Only `view=all` is re-read once the write lands — a defer cannot move a
 * row into or out of the completed list.
 */
export function tagDefer(action: ActionSummary, deferUntil: string | null): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const name = status.data.name;

  return submitOrWithdraw(
    writeHost,
    action.id,
    'deferUntil',
    { deferUntil },
    { deferUntil },
    (current) => current,
    [rereadAllFor(name)],
  ).then(() => undefined);
}

/** Test seam, like the other stores'. Production code never calls it. */
export function resetTag(): void {
  store.setState(INITIAL);
  quietRefresh.reset();
  tagMountGuard.reset();
}
